import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { logEvent } from "helpers/analytics";
import { UserTypeSelectionModal } from "./user-type-selection-modal";
import { ApplicationConfig } from "context/ApplicationConfig";
import { isLeanServicesModeEnabled } from "helpers/lean-services";

interface PromotionalTextProps {
  text: string;
  category?: string;
  loading?: boolean;
}

export const PromotionalText: React.FC<PromotionalTextProps> = ({
  text,
  category,
  loading = false,
}) => {
  const navigate = useHistory().push;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toggles } = React.useContext(ApplicationConfig);
  const isLeanServicesMode = isLeanServicesModeEnabled(toggles);

  const homeownerLink = category
    ? `/homeowners/hire/new?category=${category}&lead_source=glossary_page`
    : "/homeowners/hire/new?lead_source=glossary_page";

  const handlePlaceRequestClick = () => {
    logEvent("glossary/request_started");
    setIsModalOpen(true);
  };

  const handleSelectHomeowner = () => {
    setIsModalOpen(false);
    logEvent("glossary/user_type_selected", { type: "homeowner" });
    navigate(homeownerLink);
  };

  const handleSelectOrganization = () => {
    setIsModalOpen(false);
    logEvent("glossary/user_type_selected", { type: "organization" });
    window.open(process.env.REACT_APP_MARKETPLACE_URL, "_blank");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (isLeanServicesMode) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-flatteredFlamingo-200/5 to-flatteredFlamingo-200/10 border border-flatteredFlamingo-300/20 p-6 my-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-5 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-flatteredFlamingo-200/5 to-flatteredFlamingo-200/10 border border-flatteredFlamingo-300/20 p-6 my-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1">
          <p className="text-[#153243] text-base md:text-lg leading-7 font-medium font-[Inter]">
            {text}
          </p>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={handlePlaceRequestClick}
            className="group inline-flex items-center justify-center px-6 py-3 bg-[#ED6E5C] hover:bg-[#D85D4A] text-white hover:text-prussianBlue-300 font-medium text-sm md:text-base transition-colors duration-200 whitespace-nowrap"
          >
            <span>Place a request</span>{" "}
            <FaArrowRight className="w-3 h-3 md:w-3.5 md:h-3.5 ml-2 group-hover:group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </div>

      <UserTypeSelectionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelectHomeowner={handleSelectHomeowner}
        onSelectOrganization={handleSelectOrganization}
      />
    </div>
  );
};
